import React, { useState } from "react";
import { Link } from "react-router-dom";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { OvalLoader } from "../../../components/common/Loader";
import { BRAND, GRADIENTS } from "../../../constant/theme";
import {
  PERIOD_OPTIONS,
  getDisplayName,
  getPeriodSubtitle,
  getProfileImage,
  getSectionTitle,
} from "./topAchieversShared";
import tysIconWhite from "../../../assets/images/icons/tys_white.png";
import oneToOneIconWhite from "../../../assets/images/icons/one_to_one_white.png";
import referralsIconWhite from "../../../assets/images/icons/referrals_white.png";
import visitorsIconWhite from "../../../assets/images/icons/visitors_white.png";
import chiefGuestsIconWhite from "../../../assets/images/icons/chief_guests_white.png";
import powerDatesIconWhite from "../../../assets/images/icons/power_dates_white.png";
import inductionsIconWhite from "../../../assets/images/icons/inductions_white.png";

const ACHIEVER_ICONS_WHITE = {
  tyfcbs: tysIconWhite,
  fbms: oneToOneIconWhite,
  rfls: referralsIconWhite,
  visitors: visitorsIconWhite,
  chief_guests: chiefGuestsIconWhite,
  power_dates: powerDatesIconWhite,
  inductions: inductionsIconWhite,
};

const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatMetric = (user, cat) => {
  const value = cat.formatVal(user[cat.valKey] || 0);
  return value;
};

const CATEGORY_REPORT_ROUTES = {
  tyfcbs: "/reports/tyfcbs",
  fbms: "/reports/one-to-one",
  rfls: "/reports/referrals",
  visitors: "/reports/visitors-general",
  chief_guests: "/reports/visitors-chief-guest",
  power_dates: "/reports/visitors-power-date",
  inductions: "/reports/visitors-inductions",
};

const MemberAvatar = ({ user, cat }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const src = getProfileImage(user, cat);
  const name = getDisplayName(user, cat);

  if (!src || imgFailed) {
    return (
      <div className="ta-d4-avatar is-initials">
        {getInitials(name)}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className="ta-d4-avatar"
      onError={() => setImgFailed(true)}
    />
  );
};

const TopAchieversDesign4 = ({
  activeCategories,
  selectedWeek,
  weekOptions,
  currentData,
  currentLoading,
  excelLoading,
  handlePeriodChange,
  downloadExcel,
}) => {
  const sectionTitle = getSectionTitle(selectedWeek);

  return (
    <div className="card mb-4 custom-card border-0 rounded-4 top-achievers-section">
      {/* Top Banner Header */}
      <div
        className="card-header border-0 d-flex flex-wrap justify-content-between align-items-center gap-3"
        style={{
          backgroundColor: BRAND.primary,
          backgroundImage: GRADIENTS.topAchieversHeader,
          padding: "24px 32px",
          position: "relative",
          overflow: "hidden",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
        }}
      >
        <div style={{ position: "relative", zIndex: 1, minWidth: "220px" }}>
          <h2
            style={{
              color: "#fff",
              margin: 0,
              fontWeight: 700,
              fontSize: "1.4rem",
              letterSpacing: "-0.01em",
            }}
          >
            {sectionTitle}
          </h2>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginTop: "6px",
            }}
          >
            <p
              style={{
                color: "rgba(255, 255, 255, 0.85)",
                margin: 0,
                fontSize: "0.85rem",
                fontWeight: 500,
              }}
            >
              {getPeriodSubtitle(selectedWeek, currentData)}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            position: "relative",
            zIndex: 1,
            flexWrap: "wrap",
          }}
        >
          <div className="ta-period-pills ta-period-pills--header" role="tablist">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={selectedWeek === opt.value}
                className={`ta-period-pill${
                  selectedWeek === opt.value ? " is-active" : ""
                }`}
                onClick={() => handlePeriodChange(opt.value)}
              >
                {weekOptions?.[opt.value]?.label || opt.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            disabled={excelLoading}
            onClick={() => {
              if (!excelLoading) downloadExcel(activeCategories);
            }}
            style={{
              background: "#ffffff",
              border: "1px solid #ffffff",
              color: "#000000",
              padding: "8px 20px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: excelLoading ? "not-allowed" : "pointer",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            {excelLoading ? (
              <>
                Downloading... <i className="bx bx-loader-alt bx-spin"></i>
              </>
            ) : (
              <>
                Download{" "}
                <i className="bx bx-download" style={{ fontSize: "1.2rem" }}></i>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Cards Grid Body */}
      <div className="card-body p-4">
        {currentLoading ? (
          <div className="d-flex justify-content-center py-5">
            <OvalLoader color={BRAND.primary} />
          </div>
        ) : (
          <div className="ta-d4-grid">
            {activeCategories.map((cat) => {
              const list = (cat.list || []).slice(0, 3);
              const rank1 = list[0];
              const rank2 = list[1];
              const rank3 = list[2];

              return (
                <div key={cat.id} className="ta-d4-card">
                  {/* Card Title Header with 3D Wrapped Ribbon */}
                  <div className="ta-d4-header">
                    <div className="ta-d4-ribbon-tag">
                      {ACHIEVER_ICONS_WHITE[cat.id] && (
                        <img
                          src={ACHIEVER_ICONS_WHITE[cat.id]}
                          alt=""
                          className="ta-d4-ribbon-icon"
                        />
                      )}
                      <span className="ta-d4-ribbon-title">{cat.title}</span>
                    </div>
                    <div className="ta-d4-header-meta">
                      <span className="ta-d4-subtitle">Top 3 Achievers</span>
                    </div>
                  </div>

                  {list.length > 0 ? (
                    <div className="ta-d4-content">
                      {/* Rank 1 */}
                      {rank1 && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip
                              id={`ta-d4-${cat.id}-1`}
                              className="top-achiever-tooltip custom-brand-tooltip"
                            >
                              #1 {rank1[cat.tooltipNameKey] || rank1[cat.nameKey]}{" "}
                              ({rank1.user_company_name || ""}) -{" "}
                              {rank1.country_name || ""} - {rank1.state_name || ""} -{" "}
                              {rank1.region_name || ""} - {rank1.chapter_name || ""}
                            </Tooltip>
                          }
                        >
                          <div className="ta-d4-rank-row" tabIndex={0}>
                            <span className="ta-d4-rank">1</span>

                            <div className="ta-d4-avatar-wrap">
                              <MemberAvatar user={rank1} cat={cat} />
                            </div>

                            <div className="ta-d4-user-info">
                              <div className="ta-d4-user-name">
                                {getDisplayName(rank1, cat)}
                              </div>
                              <div className="ta-d4-user-sub">
                                {rank1.user_company_name ||
                                  rank1.chapter_name ||
                                  rank1.user_role_name ||
                                  "Member"}
                              </div>
                            </div>

                            <div className="ta-d4-metric-wrap">
                              <div className="ta-d4-metric-val">
                                {formatMetric(rank1, cat)}
                              </div>
                            </div>
                          </div>
                        </OverlayTrigger>
                      )}

                      {/* Rank 2 */}
                      {rank2 && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip
                              id={`ta-d4-${cat.id}-2`}
                              className="top-achiever-tooltip custom-brand-tooltip"
                            >
                              #2 {rank2[cat.tooltipNameKey] || rank2[cat.nameKey]}{" "}
                              ({rank2.user_company_name || ""}) -{" "}
                              {rank2.country_name || ""} - {rank2.state_name || ""} -{" "}
                              {rank2.region_name || ""} - {rank2.chapter_name || ""}
                            </Tooltip>
                          }
                        >
                          <div className="ta-d4-rank-row" tabIndex={0}>
                            <span className="ta-d4-rank">2</span>

                            <div className="ta-d4-avatar-wrap">
                              <MemberAvatar user={rank2} cat={cat} />
                            </div>

                            <div className="ta-d4-user-info">
                              <div className="ta-d4-user-name">
                                {getDisplayName(rank2, cat)}
                              </div>
                              <div className="ta-d4-user-sub">
                                {rank2.user_company_name ||
                                  rank2.chapter_name ||
                                  rank2.user_role_name ||
                                  "Member"}
                              </div>
                            </div>

                            <div className="ta-d4-metric-wrap">
                              <div className="ta-d4-metric-val">
                                {formatMetric(rank2, cat)}
                              </div>
                            </div>
                          </div>
                        </OverlayTrigger>
                      )}

                      {/* Rank 3 */}
                      {rank3 && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip
                              id={`ta-d4-${cat.id}-3`}
                              className="top-achiever-tooltip custom-brand-tooltip"
                            >
                              #3 {rank3[cat.tooltipNameKey] || rank3[cat.nameKey]}{" "}
                              ({rank3.user_company_name || ""}) -{" "}
                              {rank3.country_name || ""} - {rank3.state_name || ""} -{" "}
                              {rank3.region_name || ""} - {rank3.chapter_name || ""}
                            </Tooltip>
                          }
                        >
                          <div className="ta-d4-rank-row" tabIndex={0}>
                            <span className="ta-d4-rank">3</span>

                            <div className="ta-d4-avatar-wrap">
                              <MemberAvatar user={rank3} cat={cat} />
                            </div>

                            <div className="ta-d4-user-info">
                              <div className="ta-d4-user-name">
                                {getDisplayName(rank3, cat)}
                              </div>
                              <div className="ta-d4-user-sub">
                                {rank3.user_company_name ||
                                  rank3.chapter_name ||
                                  rank3.user_role_name ||
                                  "Member"}
                              </div>
                            </div>

                            <div className="ta-d4-metric-wrap">
                              <div className="ta-d4-metric-val">
                                {formatMetric(rank3, cat)}
                              </div>
                            </div>
                          </div>
                        </OverlayTrigger>
                      )}

                      {/* View all link */}
                      <div className="ta-d4-footer">
                        <Link
                          to={CATEGORY_REPORT_ROUTES[cat.id] || "/reports/overall"}
                          className="ta-d4-viewall-link"
                        >
                          <span>All {cat.title}</span>
                          <i className="bx bx-right-arrow-alt"></i>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="ta-d4-empty">
                      <div className="ta-d4-empty-icon">
                        <i className="bx bx-file"></i>
                      </div>
                      <div className="ta-d4-empty-text">No data found!</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopAchieversDesign4;
